import React, { useCallback, useEffect, useMemo, useState } from 'react'
import {
  Accordion,
  AccordionItem,
  Button,
  DataTable,
  InputField,
  Select,
  Tabs,
  ToggleSwitch,
  type DataTableColumn,
} from '@enamelai/design-system'
import { getEnv } from '@utils/env'
import { useSettingsHeader } from '@pages/Settings/SettingsHeader.context'
import { useNotificationToast } from '@context/NotificationToastContext'
import {
  createFailedToast,
  createSuccessToast,
} from '@utils/notificationToast.util'
import { useDashboardClinicId } from '@hooks/dashboard/useDashboardPeriod'
import type { DashboardPeriod } from '@services/api/dashboard/finance.api'
import {
  createClinicGoal,
  getClinicGoal,
  updateClinicGoal,
  type ClinicGoal,
} from '@services/api/dashboard/clinic-goal.api'
import {
  ALL_GOAL_FIELDS,
  buildGoalPayload,
  buildGoalsFromClinicGoal,
  formatGoalValue,
  GOAL_CONDITION_OPTIONS,
  GOAL_PREFIX,
  GOAL_SUFFIX,
  GOALS_SECTIONS,
  type GoalFormat,
  type GoalSection,
} from './goalsSettings.constants'
import SettingsSkeleton from '@pages/Settings/SettingsSkeleton.page'
import { useSettingsTabSkeleton } from '@pages/Settings/useSettingsTabSkeleton'
import { useSettingsSubpageLoading } from '@pages/Settings/SettingsSubpageGate.component'
import { useGlobalLoadingActions } from '@hooks/useGlobalLoading'
import { sortRows, useTableSort } from '../../shared/settingsTable'
import './GoalsStyle.css'

/** Status and Condition are not persisted by the clinic-goal API yet. */
const GOALS_CONDITIONS_ENABLED =
  getEnv('REACT_APP_GOALS_CONDITIONS_ENABLED', 'false') === 'true'

const GOALS_LOADING_CONTAINER = '.settings-goals'

const PERIOD_TABS: { id: DashboardPeriod; label: string }[] = [
  { id: 'day', label: 'Daily' },
  { id: 'week', label: 'Weekly' },
  { id: 'month', label: 'Monthly' },
  { id: 'year', label: 'Annual' },
]

type GoalsMap = Record<string, string>
type GoalMeta = { enabled: boolean; condition: string }
type GoalMetaMap = Record<string, GoalMeta>

const buildDefaultMeta = (): GoalMetaMap =>
  Object.fromEntries(
    ALL_GOAL_FIELDS.map((field) => [field, { enabled: true, condition: '' }]),
  )

type GoalRow = {
  id: string
  metricType: string
  format: GoalFormat
  target: string
  enabled: boolean
  condition: string
}

const readSortValue = (row: GoalRow, columnId: string): string => {
  if (columnId === 'target') {
    const value = Number(row.target)
    return Number.isFinite(value) && row.target !== ''
      ? value.toFixed(2).padStart(24, '0')
      : ''
  }
  if (columnId === 'enabled') return row.enabled ? '0' : '1'
  return String(row[columnId as keyof GoalRow] ?? '').toLowerCase()
}

const GoalUnit = ({ children }: { children: string }): React.ReactElement => (
  <span className="goals-unit">{children}</span>
)

interface GoalsTableProps {
  section: GoalSection
  goals: GoalsMap
  meta: GoalMetaMap
  isEditing: boolean
  onGoalChange: (id: string, value: string) => void
  onMetaChange: (id: string, patch: Partial<GoalMeta>) => void
}

const GoalsTable = ({
  section,
  goals,
  meta,
  isEditing,
  onGoalChange,
  onMetaChange,
}: GoalsTableProps): React.ReactElement => {
  const [sort, toggleSort] = useTableSort()

  const rows: GoalRow[] = useMemo(
    () =>
      sortRows(
        section.metrics.map((metric) => ({
          id: metric.id,
          metricType: metric.metricType,
          format: metric.format,
          target: goals[metric.id] ?? '',
          enabled: meta[metric.id]?.enabled ?? true,
          condition: meta[metric.id]?.condition ?? '',
        })),
        sort,
        readSortValue,
      ),
    [section.metrics, goals, meta, sort],
  )

  const columns = useMemo<DataTableColumn<GoalRow>[]>(() => {
    const statusColumn: DataTableColumn<GoalRow> = {
      id: 'enabled',
      label: 'Status',
      sortable: !isEditing,
      width: '84px',
      renderCell: (row) => (
        <ToggleSwitch
          aria-label={`${row.metricType} status`}
          checked={row.enabled}
          disabled={!isEditing}
          onChange={(checked) => onMetaChange(row.id, { enabled: checked })}
        />
      ),
    }

    const conditionColumn: DataTableColumn<GoalRow> = {
      id: 'condition',
      label: isEditing ? 'Condition (Optional)' : 'Condition',
      sortable: !isEditing,
      renderCell: (row) =>
        isEditing ? (
          <Select
            className="goals-inline-select"
            label={`${row.metricType} condition`}
            placeholder="Select"
            options={GOAL_CONDITION_OPTIONS}
            value={row.condition}
            onChange={(value) =>
              onMetaChange(row.id, { condition: String(value) })
            }
          />
        ) : (
          <span className="goals-muted">
            {GOAL_CONDITION_OPTIONS.find((o) => o.value === row.condition)
              ?.label ?? 'N/A'}
          </span>
        ),
    }

    return [
      ...(GOALS_CONDITIONS_ENABLED ? [statusColumn] : []),
      {
        id: 'metricType',
        label: 'Metric',
        sortable: !isEditing,
        width: '400px',
        renderCell: (row) => (
          <span className="goals-metric">{row.metricType}</span>
        ),
      },
      ...(GOALS_CONDITIONS_ENABLED ? [conditionColumn] : []),
      {
        id: 'target',
        label: 'Target',
        sortable: !isEditing,
        renderCell: (row) => {
          const prefix = GOAL_PREFIX[row.format]
          const suffix = GOAL_SUFFIX[row.format]
          if (isEditing) {
            return (
              <InputField
                className="goals-inline-input"
                variant="text"
                inputMode="decimal"
                aria-label={`${row.metricType} target`}
                placeholder="0"
                clearable={false}
                fullWidth
                leadingIcon={prefix ? <GoalUnit>{prefix}</GoalUnit> : undefined}
                trailingIcon={
                  suffix ? <GoalUnit>{suffix}</GoalUnit> : undefined
                }
                value={row.target}
                onChange={(e) =>
                  onGoalChange(row.id, e.target.value.replace(/[^\d.]/g, ''))
                }
              />
            )
          }
          const value = formatGoalValue(row.target, row.format)
          return (
            <span className="goals-target">
              {prefix ? <GoalUnit>{prefix}</GoalUnit> : null}
              <span>{value}</span>
              {suffix && value !== '-' ? <span>{suffix}</span> : null}
            </span>
          )
        },
      },
    ]
  }, [isEditing, onGoalChange, onMetaChange])

  return (
    <DataTable
      className="goals-table"
      columns={columns}
      rows={rows}
      showActions={false}
      sortColumnId={sort.columnId}
      sortDirection={sort.direction}
      onSort={toggleSort}
    />
  )
}

const Goals = (): React.ReactElement => {
  const { setHeaderButtons } = useSettingsHeader()
  const { showToast } = useNotificationToast()
  const { showLoading, hideLoading } = useGlobalLoadingActions()
  const clinicId = useDashboardClinicId()

  const [period, setPeriod] = useState<DashboardPeriod>('day')
  const isTabSkeleton = useSettingsTabSkeleton(period)
  const [clinicGoal, setClinicGoal] = useState<ClinicGoal | null>(null)
  const [savedGoals, setSavedGoals] = useState<GoalsMap>(() =>
    buildGoalsFromClinicGoal(null),
  )
  const [goals, setGoals] = useState<GoalsMap>(() =>
    buildGoalsFromClinicGoal(null),
  )
  const [savedMeta, setSavedMeta] = useState<GoalMetaMap>(buildDefaultMeta)
  const [meta, setMeta] = useState<GoalMetaMap>(buildDefaultMeta)
  const [isEditing, setIsEditing] = useState(false)
  const [loading, setLoading] = useState(true)
  const [dataReady, setDataReady] = useState(false)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)

  useSettingsSubpageLoading(!dataReady)

  const isDirty = useMemo(
    () =>
      JSON.stringify(goals) !== JSON.stringify(savedGoals) ||
      JSON.stringify(meta) !== JSON.stringify(savedMeta),
    [goals, savedGoals, meta, savedMeta],
  )

  useEffect(() => {
    if (!clinicId) {
      setError('No clinic selected')
      setLoading(false)
      setDataReady(true)
      return
    }

    let cancelled = false
    setLoading(true)
    setError(null)

    getClinicGoal({ clinicId, date: new Date().toISOString(), period })
      .then((goal) => {
        if (cancelled) return
        setClinicGoal(goal)
        const map = buildGoalsFromClinicGoal(goal)
        setSavedGoals(map)
        setGoals(map)
      })
      .catch((e: unknown) => {
        if (cancelled) return
        setError(e instanceof Error ? e.message : 'Failed to load goals')
        setClinicGoal(null)
      })
      .finally(() => {
        if (cancelled) return
        setLoading(false)
        setDataReady(true)
      })

    return () => {
      cancelled = true
    }
  }, [clinicId, period])

  const handleGoalChange = useCallback((id: string, value: string): void => {
    setGoals((prev) => ({ ...prev, [id]: value }))
  }, [])

  const handleMetaChange = useCallback(
    (id: string, patch: Partial<GoalMeta>): void => {
      setMeta((prev) => ({ ...prev, [id]: { ...prev[id], ...patch } }))
    },
    [],
  )

  const handleCancel = useCallback((): void => {
    setGoals({ ...savedGoals })
    setMeta(structuredClone(savedMeta))
    setIsEditing(false)
  }, [savedGoals, savedMeta])

  const handleSave = useCallback(async (): Promise<void> => {
    if (!clinicId) {
      showToast(
        createFailedToast(
          'Unable to save goals',
          'No clinic is selected. Please select a clinic and try again.',
        ),
      )
      return
    }

    setSaving(true)
    showLoading({ container: GOALS_LOADING_CONTAINER })
    try {
      const payload = buildGoalPayload(goals)
      const updated = clinicGoal
        ? await updateClinicGoal({
            clinic_goal_id: clinicGoal.clinic_goal_id,
            ...payload,
          })
        : await createClinicGoal({ clinic_id: clinicId, period, ...payload })

      setClinicGoal(updated)
      const map = buildGoalsFromClinicGoal(updated)
      setSavedGoals(map)
      setGoals(map)
      setSavedMeta(structuredClone(meta))
      setIsEditing(false)
      showToast(
        createSuccessToast(
          'Goals saved',
          'Goal targets have been updated successfully.',
        ),
      )
    } catch (e: unknown) {
      showToast(
        createFailedToast(
          'Unable to save goals',
          e instanceof Error
            ? e.message
            : 'Something went wrong while saving. Please try again.',
        ),
      )
    } finally {
      setSaving(false)
      hideLoading(GOALS_LOADING_CONTAINER)
    }
  }, [
    clinicId,
    clinicGoal,
    goals,
    hideLoading,
    meta,
    period,
    showLoading,
    showToast,
  ])

  const canEdit = !error && !loading

  useEffect(() => {
    setHeaderButtons(
      isEditing ? (
        <>
          <Button
            className="settings-ds-button"
            variant="secondary"
            size="md"
            type="button"
            onClick={handleCancel}
            disabled={saving}
          >
            Cancel
          </Button>
          <Button
            className="settings-ds-button"
            variant="primary"
            size="md"
            type="button"
            onClick={() => void handleSave()}
            disabled={!isDirty || saving}
          >
            {saving ? 'Saving…' : 'Save changes'}
          </Button>
        </>
      ) : (
        <Button
          className="settings-ds-button"
          variant="primary"
          size="md"
          type="button"
          onClick={() => setIsEditing(true)}
          disabled={!canEdit}
        >
          Edit info
        </Button>
      ),
    )

    return () => setHeaderButtons(null)
  }, [
    canEdit,
    handleCancel,
    handleSave,
    isDirty,
    isEditing,
    saving,
    setHeaderButtons,
  ])

  const handlePeriodChange = (next: string): void => {
    if (next !== period) setPeriod(next as DashboardPeriod)
  }

  return (
    <div className="settings-goals">
      <Tabs
        className="goals-period-tabs"
        items={PERIOD_TABS}
        value={period}
        disabled={saving || (isEditing && isDirty)}
        onChange={handlePeriodChange}
      />

      {error ? (
        <p className="goals-status-error" role="alert">
          {error}
        </p>
      ) : isTabSkeleton || loading ? (
        <SettingsSkeleton variant="goals" showTabs={false} />
      ) : (
        <Accordion
          className="goals-accordion"
          defaultOpenIds={GOALS_SECTIONS[0] ? [GOALS_SECTIONS[0].id] : []}
        >
          {GOALS_SECTIONS.map((section) => (
            <AccordionItem
              key={section.id}
              id={section.id}
              title={section.title}
            >
              <GoalsTable
                section={section}
                goals={goals}
                meta={meta}
                isEditing={isEditing}
                onGoalChange={handleGoalChange}
                onMetaChange={handleMetaChange}
              />
            </AccordionItem>
          ))}
        </Accordion>
      )}
    </div>
  )
}

export default Goals
